/**
 * harness/useDraggableOrder.js
 * T-Cron-2026-10-25 02:00 早间 (round-1-of-day) — 改进清单 #7「拖拽排序」上生产线
 *
 * 背景(为什么这一轮做这个):
 *   根目录老仓库(components/Sidebar.jsx,T-Cron-2026-10-10 08:00 / T77)
 *   早就实现了侧栏「书籍 / 热点 / 角度 / 来源」拖拽排序,本地探针确认:
 *     OLD-FORK = YES,VITE-PROD = NO
 *   线上 canonical bundle 里 `sidebar-item-drag-handle` / `aria-grabbed` /
 *   `xhs-kanban-book-order` 全部 ABSENT ⇒ 这个功能**从来没上线过**,
 *   改进清单 #7 在生产环境是 0%,不是「仅书籍部分完成」。
 *   ⇒ 本轮把已验证的 hook 移植进 Vite 生产树。
 *
 * 设计契约(与老仓库 T77 实现保持一致,便于两边行为对齐):
 *   · localStorage 保存顺序,key 每类列表独立(改书籍不影响热点);
 *   · 上游新增的项追加到末尾 —— 内容刷新后用户排好的顺序不被冲掉;
 *   · 拖拽只在 `(hover: hover) and (pointer: fine)` 设备上启用。
 *     HTML5 drag-and-drop 在触屏上根本不会触发,与其让它在移动端
 *     变成一个点不动的假 affordance,不如直接关掉(老仓库同款闸门);
 *   · 键盘可达:聚焦某行后 Alt+↑ / Alt+↓ 也能上下移动该行
 *     (aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown")—— 拖拽不能是
 *     唯一的重排途径,否则读屏/纯键盘用户就完全用不了这个功能;
 *   · 拖拽全程不碰 React 渲染主干:排序结果是一个普通数组,交给
 *     useMemo 派生,不影响任何既有 filter 逻辑。
 *
 * 注意:本文件用 React,所以**必须显式 import React**
 *      (vite.config.js 的 esbuild.jsxInject 只对 .jsx 生效,.js 拿不到注入)。
 */
import React from 'react';

/** 读取保存过的顺序。坏数据一律当空数组处理,不能让一个坏 localStorage 值白屏。 */
function loadSavedOrder(key) {
  try {
    if (typeof localStorage === 'undefined' || !key) return [];
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === 'string') : [];
  } catch (_) {
    return [];
  }
}

/** 写回顺序。localStorage 写满 / 隐私模式下 setItem 会抛,静默吞掉。 */
function saveOrder(key, order) {
  try {
    if (typeof localStorage === 'undefined' || !key) return;
    localStorage.setItem(key, JSON.stringify(order));
  } catch (_) {
    /* 顺序只是锦上添花,存不下不该影响主流程 */
  }
}

/**
 * 拖拽排序 hook。
 *
 * @param {string} storageKey   localStorage key(每类列表一个)
 * @param {Array}  list         上游列表,元素形如 { name, count }
 * @param {Function} [extractName] 从元素里取名字的函数,默认读 .name
 * @returns {{
 *   items: Array,            按保存顺序排好的列表
 *   draggedName: string|null, 当前正在拖的行(纯视觉)
 *   dragOverName: string|null, 当前悬停的落点(纯视觉)
 *   supportsDrag: boolean,   是否为 hover-fine 设备
 *   dragHandlers: object,    展开到列表容器上的 props 集合
 * }}
 */
export function useDraggableOrder(storageKey, list, extractName) {
  const normName = React.useCallback(
    (item) => {
      const n = extractName ? extractName(item) : null;
      if (n != null) return n;
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') return item.name || item.value || '';
      return '';
    },
    [extractName]
  );

  // hover-fine 闸门:触屏设备上 HTML5 DnD 不会触发,直接不启用。
  const [supportsDrag, setSupportsDrag] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    setSupportsDrag(mq.matches);
    const onChange = (e) => setSupportsDrag(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  const [order, setOrder] = React.useState(() => loadSavedOrder(storageKey));
  const [draggedName, setDraggedName] = React.useState(null);
  const [dragOverName, setDragOverName] = React.useState(null);

  // 上游列表的「签名」——只依赖名字序列,内容(count 变化)不影响顺序。
  const upstreamKey = React.useMemo(
    () => (list || []).map(normName).filter(Boolean).join('|'),
    [list, normName]
  );

  // 上游变化时:剔除已消失的项,新增的项追加到末尾。
  React.useEffect(() => {
    setOrder((prev) => {
      const names = new Set((list || []).map(normName).filter(Boolean));
      const filtered = prev.filter((n) => names.has(n));
      (list || []).forEach((it) => {
        const n = normName(it);
        if (n && !filtered.includes(n)) filtered.push(n);
      });
      return filtered;
    });
  }, [upstreamKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const reorder = React.useCallback((fromName, toName) => {
    if (!fromName || !toName || fromName === toName) return;
    setOrder((prev) => {
      const allNames = (list || []).map(normName).filter(Boolean);
      const base = prev.length ? prev.filter((n) => allNames.includes(n)) : allNames;
      const ordered = base.length === allNames.length ? base.slice() : allNames.slice();
      allNames.forEach((n) => { if (!ordered.includes(n)) ordered.push(n); });
      const fromIdx = ordered.indexOf(fromName);
      const toIdx = ordered.indexOf(toName);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = ordered.slice();
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, fromName);
      saveOrder(storageKey, next);
      return next;
    });
  }, [list, normName, storageKey]);

  // 按保存顺序重排;上游有但 order 里没有的项按原顺序补在末尾。
  const items = React.useMemo(() => {
    const src = list || [];
    if (!src.length) return src;
    const byName = new Map(src.map((it) => [normName(it), it]));
    const result = [];
    const seen = new Set();
    order.forEach((name) => {
      if (byName.has(name) && !seen.has(name)) {
        result.push(byName.get(name));
        seen.add(name);
      }
    });
    src.forEach((it) => {
      const name = normName(it);
      if (name && !seen.has(name)) {
        result.push(it);
        seen.add(name);
      }
    });
    return result;
  }, [list, order, normName]);

  const onDragStart = React.useCallback((name) => setDraggedName(name), []);
  const onDragOverName = React.useCallback((name) => setDragOverName(name), []);
  const onDragEnd = React.useCallback(() => {
    setDraggedName(null);
    setDragOverName(null);
  }, []);
  const onDrop = React.useCallback((from, to) => {
    reorder(from, to);
    setDraggedName(null);
    setDragOverName(null);
  }, [reorder]);

  // 键盘重排:把第 fromIdx 项挪到 dir 方向相邻位置。
  const onReorderKey = React.useCallback((from, dir) => {
    const list2 = items || [];
    const idx = list2.findIndex((it) => normName(it) === from);
    if (idx === -1) return;
    const target = idx + dir;
    if (target < 0 || target >= list2.length) return;
    reorder(from, normName(list2[target]));
  }, [items, normName, reorder]);

  return {
    items,
    draggedName,
    dragOverName,
    supportsDrag,
    dragHandlers: {
      draggable: supportsDrag,
      // 键盘重排**不受 pointer 闸门限制**。
      //
      // 闸门(`hover:hover and pointer:fine`)存在的唯一理由是:
      // HTML5 drag-and-drop 在触屏上根本不触发,与其留一个点不动的
      // 假 affordance,不如关掉拖拽本身。
      //
      // 但 Alt+↑/↓ 是纯键盘手势,在触屏外接键盘(iPad+Magic Keyboard、
      // Android+蓝牙键盘)上**完全可用**。如果把键盘路径也一起关掉,
      // 这类设备就一条重排途径都没有了 —— 触屏 + 键盘用户反而比
      // 纯桌面用户更弱。这违背了本 hook 自己的设计契约:
      // 「拖拽不能是唯一的重排途径」。
      //
      // 所以这里拆成两个开关:
      //   draggable —— 视觉 + HTML5 DnD(仅 fine pointer)
      //   reorderable —— 键盘 Alt+↑/↓(所有设备)
      reorderable: true,
      draggedName,
      dragOverName,
      onDragStart,
      onDragOverName,
      onDragEnd,
      onDrop,
      onReorderKey,
      // 排序提示两种设备都显示:fine pointer 上提示拖拽手柄,
      // 其它设备上提示 Alt+↑/↓ 键盘手势。
      sortHint: true,
    },
  };
}

export default useDraggableOrder;
